import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { SpaceBetween, Alert } from '@cloudscape-design/components';
import ProcessingSection from './ProcessingSection.jsx';
import ResultsSection from './ResultsSection.jsx';
import PreviewSection from './PreviewSection.jsx';
import FeedbackModal from './FeedbackModal.jsx';
import ThemeToggle from './ThemeToggle.jsx';
import { useAuth } from '../../auth/AuthProvider.jsx';
import './image-studio.css';

const OPERATIONS = [
  { id: 'resize', label: 'Resize Images', desc: 'Change dimensions', icon: '🖼️', active: true },
  { id: 'background', label: 'Background', desc: 'Remove or replace', icon: '✂️', active: true },
  { id: 'lifestyle', label: 'Lifestyle Images', desc: 'AI product scenes', icon: '📸', active: true },
  { id: 'convert', label: 'Convert Format', desc: 'Change file type', icon: '🔄', active: true },
];

export default function ImageStudio() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  // Admin-only operations inside Image Studio. Today: Lifestyle Images
  // (Flux-driven AI scene generation, gated while it stabilises). Resize,
  // Background, and Convert remain accessible to everyone. Non-admins see
  // the Lifestyle tab in the strip but can't click it.
  const OPERATIONS_FOR_USER = OPERATIONS.map(op =>
    op.id === 'lifestyle' && !isAdmin ? { ...op, active: false } : op
  );
  const [activeOp, setActiveOp] = useState('resize');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [lastCount, setLastCount] = useState(0);
  const [error, setError] = useState('');
  const [previewRunId, setPreviewRunId] = useState(null);
  const [previewOperation, setPreviewOperation] = useState(null);
  const [username, setUsername] = useState('unknown');
  // True whenever ProcessingSection has an active draft (files selected,
  // preview in flight, or a generation running). When true, PreviewSection
  // is hidden so the user sees only one of "building next run" vs
  // "reviewing last run" at a time.
  const [isDrafting, setIsDrafting] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const [allRuns, setAllRuns] = useState([]);

  // Resolve the Harmony username once at mount. The Harmony shell exposes a
  // `window.harmony.api.getUserName()` synchronous accessor, but it may not
  // be ready on first paint — retry briefly. We set it once here and share
  // it with all children so nothing downstream has to duplicate the logic.
  useEffect(() => {
    let cancelled = false;
    const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    (async () => {
      let retries = 5;
      while (!cancelled && retries-- > 0) {
        const user = window?.harmony?.api?.getUserName?.();
        if (user) { if (!cancelled) setUsername(user); return; }
        await wait(300);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleProcessComplete = (metadata) => {
    setRefreshTrigger(prev => prev + 1);
    setLastCount(metadata?.total_images || 0);
    setError('');
    // Auto-select the just-completed run as the preview
    if (metadata?.run_id) {
      setPreviewRunId(metadata.run_id);
      setPreviewOperation(metadata.operation || activeOp);
    }
    // Username comes from Harmony only (see the useEffect above). The run
    // metadata's `user` field records who created the run, which is a
    // different thing and must not overwrite the logged-in user.
  };

  const handleRunSelect = (runId, operation) => {
    setPreviewRunId(runId);
    setPreviewOperation(operation);
  };

  const handleRunsLoaded = (runs) => {
    setAllRuns(runs || []);
  };

  // Pick the latest completed run for a given operation from the loaded list.
  // Runs come back sorted newest-first from the API, so .find() gives us the
  // latest match directly.
  const findLatestRunForOp = (runs, op) => {
    if (!runs || runs.length === 0) return null;
    return runs.find(r => r.operation === op) || null;
  };

  // When the active tool changes, always auto-select the latest run for that
  // tool — this is the core behavior the user asked for.
  useEffect(() => {
    const latest = findLatestRunForOp(allRuns, activeOp);
    if (latest) {
      setPreviewRunId(latest.run_id);
      setPreviewOperation(latest.operation);
    } else {
      // No runs yet for this tool — clear the preview so we don't show a
      // stale run from a different tool.
      setPreviewRunId(null);
      setPreviewOperation(null);
    }
  }, [activeOp]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the runs list reloads (e.g., after a new job completes), refresh the
  // auto-selection only if the current preview is still aligned with the
  // active tool. This preserves explicit manual cross-tool selections from
  // the history table: if a user clicked a historical lifestyle run while
  // viewing the Resize tool, we don't want a new resize job to clobber it.
  useEffect(() => {
    if (!allRuns || allRuns.length === 0) return;
    if (previewOperation && previewOperation !== activeOp) return;
    const latest = findLatestRunForOp(allRuns, activeOp);
    if (latest && latest.run_id !== previewRunId) {
      setPreviewRunId(latest.run_id);
      setPreviewOperation(latest.operation);
    }
  }, [allRuns]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="image-studio-page">
      <div className="studio-container">
        <ThemeToggle />
        {/* Breadcrumb — navigates back to the SmartBiz Toolkit home via
            react-router-dom so it stays an SPA transition. Rendered as a
            real <a href> via <Link> so right-click → Open in new tab works
            (a <button onClick={navigate}> doesn't show that menu entry). */}
        <Link
          to="/"
          className="studio-back-link"
          aria-label="Back to SmartBiz Toolkit"
        >
          ← Back to SmartBiz Toolkit
        </Link>
        {/* Hero banner */}
        <div className="studio-hero">
          <div className="studio-hero-left">
            <div className="studio-hero-icon">🎨</div>
            <div className="studio-hero-text">
              <h1 className="studio-hero-title">Image Studio</h1>
              <p className="studio-hero-subtitle">Bulk edit your product images in seconds</p>
              <div className="studio-hero-status">
                <span className="studio-status-dot"></span>
                <span>Ready to process</span>
                <span className="studio-status-sep">|</span>
                <span>Supports JPG, PNG, GIF, WebP, BMP, TIFF, SVG</span>
              </div>
            </div>
          </div>
          <div className="studio-hero-ops">
            {OPERATIONS_FOR_USER.map(op => (
              <button
                key={op.id}
                className={`studio-op-tab ${activeOp === op.id ? 'active' : ''} ${!op.active ? 'disabled' : ''}`}
                onClick={() => op.active && setActiveOp(op.id)}
              >
                <span className="studio-op-icon">{op.icon}</span>
                <span className="studio-op-label">{op.label}</span>
                <span className="studio-op-desc">{op.active ? op.desc : 'Coming Soon'}</span>
              </button>
            ))}
            <button
              type="button"
              className="studio-feedback-btn"
              onClick={() => setFeedbackOpen(true)}
              title="Share feedback about Image Studio"
              aria-label="Share feedback"
            >
              <span className="studio-feedback-icon">💬</span>
              <span className="studio-feedback-label">Feedback</span>
            </button>
          </div>
        </div>
        {lastCount > 0 && (
          <div className="studio-last-session">🕐 Last session: {lastCount} images processed</div>
        )}

        {/* Content */}
        <div className="studio-content">
          <SpaceBetween size="l">
            {error && <Alert type="error" dismissible onDismiss={() => setError('')}>{error}</Alert>}
            <ProcessingSection
              operation={activeOp}
              username={username}
              onProcessComplete={handleProcessComplete}
              onActivityChange={setIsDrafting}
            />
            {!isDrafting && (
              <PreviewSection
                runId={previewRunId}
                operation={previewOperation}
                username={username}
                onRegenerateComplete={(newRunId) => {
                  setPreviewRunId(newRunId);
                  setPreviewOperation('lifestyle');
                  setRefreshTrigger(prev => prev + 1);
                }}
              />
            )}
            <ResultsSection
              refreshTrigger={refreshTrigger}
              selectedRunId={previewRunId}
              onRunSelect={handleRunSelect}
              onRunsLoaded={handleRunsLoaded}
            />
          </SpaceBetween>
        </div>
      </div>
      <FeedbackModal
        visible={feedbackOpen}
        onDismiss={() => setFeedbackOpen(false)}
        username={username}
      />
    </div>
  );
}
